// src/pages/nafed/pmc/NafedDprGenerator.js
// Manual PMC DPR publishing desk and shared PACS final-signature PDF helper.

import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import toast from "react-hot-toast";
import { db, storage } from "../../../firebaseConfig";

const MAX_DPR_BYTES = 75 * 1024 * 1024;
const DPR_WORKFLOW_VERSION = "PMC_MANUAL_DPR_V1_2026_09";

const clean = (value = "") => String(value ?? "").trim();
const pdfText = (value = "") => clean(value).replace(/[^\x20-\x7E]/g, "?");
const upper = (value = "") => clean(value).toUpperCase();
const pick = (source = {}, keys = [], fallback = "") => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && clean(value) !== "") return value;
  }
  return fallback;
};
const safeName = (value = "file") =>
  clean(value || "file")
    .replace(/[^a-z0-9._-]+/gi, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "") || "file";
const pacsOwnerUid = (project = {}) =>
  pick(project, [
    "pacsUid",
    "submittedByUid",
    "pacsUserId",
    "pacsId",
    "applicantUid",
    "ownerUid",
    "createdByUid",
    "uid",
  ]);
const projectName = (project = {}) =>
  pick(project, ["societyName", "pacsName", "nameOfSociety", "applicantName"], "PACS / Society");
const applicationId = (project = {}) =>
  pick(project, ["applicationId", "projectId", "applicationNumber"], project.id || "PROJECT");
const bytesText = (bytes = 0) => {
  const value = Number(bytes || 0);
  if (!value) return "—";
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(2)} MB`;
};
const dateText = (value) => {
  if (!value) return "—";
  const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export function isMouFullyExecuted(project = {}) {
  const status = upper(
    pick(project, ["mouStatus", "mouStage", "mouExecutionStatus"]),
  );
  return (
    ["FULLY_EXECUTED", "EXECUTED", "COMPLETED", "PACS_SIGNED"].includes(status) ||
    project.mouFullyExecuted === true ||
    Boolean(
      project.finalMouUrl &&
        (project.pacsMouSignedAt || project.mouSignatures?.pacs?.signedAt),
    )
  );
}

export async function downloadFile(url, fileName = "document.pdf") {
  if (!url) return;
  try {
    const response = await fetch(url, { cache: "no-store" });
    if (!response.ok) throw new Error(`Download failed (${response.status}).`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = safeName(fileName);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
  } catch (error) {
    console.warn("Direct DPR download fallback used:", error);
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

async function fetchBytes(url, label) {
  if (!url) throw new Error(`${label} is missing.`);
  const response = await fetch(url, { cache: "no-store" });
  if (!response.ok) throw new Error(`${label} could not be downloaded (${response.status}).`);
  return {
    bytes: new Uint8Array(await response.arrayBuffer()),
    contentType: response.headers.get("content-type") || "",
  };
}

async function embedImage(pdf, bytes, contentType = "") {
  const isPng =
    contentType.includes("png") ||
    (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47);
  const isJpeg =
    contentType.includes("jpeg") ||
    contentType.includes("jpg") ||
    (bytes[0] === 0xff && bytes[1] === 0xd8);
  if (isPng) return pdf.embedPng(bytes);
  if (isJpeg) return pdf.embedJpg(bytes);
  throw new Error("Signature must be a PNG or JPG image.");
}

function fitImage(image, maxWidth, maxHeight) {
  const scale = Math.min(maxWidth / image.width, maxHeight / image.height, 1);
  return { width: image.width * scale, height: image.height * scale };
}

function wrapPdfText(textValue, font, fontSize, maxWidth) {
  const words = pdfText(textValue).split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  words.forEach((word) => {
    const next = line ? `${line} ${word}` : word;
    if (font.widthOfTextAtSize(next, fontSize) <= maxWidth) {
      line = next;
    } else {
      if (line) lines.push(line);
      line = word;
    }
  });
  if (line) lines.push(line);
  return lines;
}

function centerText(page, value, font, size, y, color) {
  const width = page.getWidth();
  const printable = pdfText(value);
  const textWidth = font.widthOfTextAtSize(printable, size);
  page.drawText(printable, {
    x: Math.max(36, (width - textWidth) / 2),
    y,
    font,
    size,
    color,
  });
}

/**
 * Keeps the complete PMC-prepared and PMC-signed PDF untouched, then appends one
 * controlled PACS acceptance page at the true end of the final DPR.
 */
export async function buildFinalSignedDprPdf({
  sourcePdfUrl,
  pacsSignatureFile,
  dpr = {},
  project = {},
  pacsSignerName = "",
  pacsSignerDesignation = "Authorised Signatory",
}) {
  if (!pacsSignatureFile) throw new Error("Select the PACS signature image.");
  const [source, pacsBytes] = await Promise.all([
    fetchBytes(sourcePdfUrl, "PMC uploaded DPR"),
    pacsSignatureFile.arrayBuffer(),
  ]);

  const pdf = await PDFDocument.load(source.bytes, {
    ignoreEncryption: false,
    updateMetadata: false,
  });
  const [regular, bold] = await Promise.all([
    pdf.embedFont(StandardFonts.Helvetica),
    pdf.embedFont(StandardFonts.HelveticaBold),
  ]);
  const pacsImage = await embedImage(
    pdf,
    new Uint8Array(pacsBytes),
    pacsSignatureFile.type,
  );

  const page = pdf.addPage([595.28, 841.89]);
  const navy = rgb(0.035, 0.18, 0.34);
  const green = rgb(0.04, 0.42, 0.24);
  const ink = rgb(0.09, 0.16, 0.23);
  const muted = rgb(0.35, 0.42, 0.48);
  const line = rgb(0.72, 0.78, 0.83);
  const pale = rgb(0.95, 0.98, 0.96);
  const width = page.getWidth();
  const height = page.getHeight();

  page.drawRectangle({ x: 0, y: height - 12, width, height: 12, color: green });
  page.drawRectangle({ x: 0, y: 0, width, height: 7, color: navy });
  centerText(page, "PACS DPR ACCEPTANCE & EXECUTION", bold, 18, height - 68, navy);
  centerText(page, "Final page appended to the complete PMC-signed Detailed Project Report", regular, 10, height - 88, muted);
  page.drawLine({ start: { x: 42, y: height - 108 }, end: { x: width - 42, y: height - 108 }, thickness: 1.4, color: green });

  const society = projectName(project) || dpr.societyName || "PACS / Society";
  const appId = applicationId(project) || dpr.applicationId || dpr.projectId || "—";
  const version = dpr.currentVersion || dpr.version || "V001";
  const details = [
    ["PACS / Society", society],
    ["Application ID", appId],
    ["DPR Version", version],
    ["Original File", dpr.fileName || "PMC Uploaded DPR.pdf"],
  ];
  details.forEach(([label, value], index) => {
    const x = index % 2 === 0 ? 48 : 304;
    const y = height - 145 - (index > 1 ? 54 : 0);
    page.drawText(label.toUpperCase(), { x, y, font: bold, size: 7.5, color: muted });
    const lines = wrapPdfText(value, bold, 10.5, 235).slice(0, 2);
    lines.forEach((item, lineIndex) =>
      page.drawText(item, { x, y: y - 17 - lineIndex * 12, font: bold, size: 10.5, color: ink }),
    );
  });

  page.drawRectangle({ x: 42, y: 491, width: width - 84, height: 104, color: pale, borderColor: line, borderWidth: 1 });
  page.drawText("PACS ACCEPTANCE DECLARATION", { x: 58, y: 565, font: bold, size: 10, color: green });
  const declaration =
    "The PACS / Project Owner confirms receipt and review of the complete Detailed Project Report identified above. The preceding source PDF was prepared, approved and signed by the appointed PMC before publication in the portal. By signing this final page, the PACS accepts the DPR for onward appraisal, subject to verification by the lender and competent authorities.";
  wrapPdfText(declaration, regular, 10, width - 116).forEach((item, index) =>
    page.drawText(item, { x: 58, y: 544 - index * 15, font: regular, size: 10, color: ink }),
  );

  page.drawRectangle({ x: 42, y: 444, width: width - 84, height: 31, borderColor: line, borderWidth: 1, color: rgb(0.98, 0.99, 1) });
  centerText(page, "PMC AUTHORISATION AND SIGNATURE ARE CONTAINED IN THE PRECEDING DPR PAGES", bold, 8.5, 455, navy);

  const drawPacsSignatureBlock = ({ image, name, designation }) => {
    const boxWidth = 405;
    const x = (width - boxWidth) / 2;
    const boxY = 177;
    const boxHeight = 238;
    page.drawRectangle({ x, y: boxY, width: boxWidth, height: boxHeight, borderColor: line, borderWidth: 1.1, color: rgb(1, 1, 1) });
    page.drawRectangle({ x, y: boxY + boxHeight - 38, width: boxWidth, height: 38, color: navy });
    const title = "FOR PACS / PROJECT OWNER";
    const titleWidth = bold.widthOfTextAtSize(title, 10);
    page.drawText(title, { x: x + (boxWidth - titleWidth) / 2, y: boxY + boxHeight - 24, font: bold, size: 10, color: rgb(1, 1, 1) });
    const fitted = fitImage(image, 230, 88);
    page.drawImage(image, {
      x: x + (boxWidth - fitted.width) / 2,
      y: boxY + 100,
      width: fitted.width,
      height: fitted.height,
    });
    page.drawLine({ start: { x: x + 65, y: boxY + 91 }, end: { x: x + boxWidth - 65, y: boxY + 91 }, thickness: 0.8, color: muted });
    const displayName = pdfText(name) || "Authorised Signatory";
    const nameLines = wrapPdfText(displayName, bold, 10.5, boxWidth - 70).slice(0, 2);
    nameLines.forEach((item, index) => {
      const itemWidth = bold.widthOfTextAtSize(item, 10.5);
      page.drawText(item, { x: x + (boxWidth - itemWidth) / 2, y: boxY + 69 - index * 12, font: bold, size: 10.5, color: ink });
    });
    const role = pdfText(designation) || "Authorised Signatory";
    const roleWidth = regular.widthOfTextAtSize(role, 9);
    page.drawText(role, { x: x + Math.max(18, (boxWidth - roleWidth) / 2), y: boxY + 39, font: regular, size: 9, color: muted });
    const signed = `Signed: ${new Date().toLocaleDateString("en-IN")}`;
    const signedWidth = regular.widthOfTextAtSize(signed, 8.5);
    page.drawText(signed, { x: x + (boxWidth - signedWidth) / 2, y: boxY + 19, font: regular, size: 8.5, color: muted });
  };

  drawPacsSignatureBlock({
    image: pacsImage,
    name: pacsSignerName,
    designation: pacsSignerDesignation,
  });

  centerText(page, "This PACS execution page is appended by the portal; every PMC-uploaded and PMC-signed source page remains unchanged.", regular, 8, 145, muted);
  page.drawLine({ start: { x: 42, y: 123 }, end: { x: width - 42, y: 123 }, thickness: 0.7, color: line });
  page.drawText(`Workflow: ${DPR_WORKFLOW_VERSION}`, { x: 42, y: 103, font: regular, size: 7.5, color: muted });
  const pageLabel = `Final page ${pdf.getPageCount()} of ${pdf.getPageCount()}`;
  page.drawText(pageLabel, { x: width - 42 - regular.widthOfTextAtSize(pageLabel, 7.5), y: 103, font: regular, size: 7.5, color: muted });
  page.drawText("Document integrity: retain this page together with all preceding DPR pages.", { x: 42, y: 80, font: bold, size: 8.5, color: navy });

  pdf.setTitle(`Final DPR - ${society}`);
  pdf.setSubject("PMC-signed manual DPR with appended PACS acceptance signature");
  pdf.setCreator("NAFED PMC-PACS Portal");
  pdf.setProducer("NAFED PMC-PACS Portal");
  pdf.setModificationDate(new Date());
  const output = await pdf.save({ useObjectStreams: true });
  return {
    blob: new Blob([output], { type: "application/pdf" }),
    fileName: `FINAL_DPR_${safeName(appId)}_${safeName(version)}.pdf`,
    pageCount: pdf.getPageCount(),
  };
}

function validatePdf(file) {
  if (!file) return "Select the final DPR PDF.";
  if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
    return "Only a PDF file can be published as DPR.";
  }
  if (file.size > MAX_DPR_BYTES) return "DPR PDF must be smaller than 75 MB.";
  return "";
}

export default function NafedDprGenerator({
  projects = [],
  profile = {},
  onProjectUpdated,
}) {
  const eligibleProjects = useMemo(
    () => projects.filter((item) => item?.id),
    [projects],
  );
  const [projectId, setProjectId] = useState(eligibleProjects[0]?.id || "");
  const selected = useMemo(
    () =>
      eligibleProjects.find((item) => item.id === projectId) ||
      eligibleProjects[0] ||
      null,
    [eligibleProjects, projectId],
  );
  const [published, setPublished] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [dprFile, setDprFile] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!projectId && eligibleProjects[0]?.id) setProjectId(eligibleProjects[0].id);
  }, [eligibleProjects, projectId]);

  useEffect(() => {
    if (!selected?.id) {
      setPublished(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    return onSnapshot(
      doc(db, "published_dprs", selected.id),
      (snapshot) => {
        const data = snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
        setPublished(data);
        setLoading(false);
      },
      (error) => {
        console.error("Manual DPR listener failed:", error);
        setPublished(null);
        setLoading(false);
      },
    );
  }, [selected?.id]);

  const mouReady = isMouFullyExecuted(selected || {});

  async function publishManualDpr() {
    if (!selected?.id) return toast.error("Select a PACS project first.");
    const pdfError = validatePdf(dprFile);
    if (pdfError) return toast.error(pdfError);
    if (!confirmed) return toast.error("Confirm the DPR verification declaration.");
    if (!mouReady) return toast.error("Complete the PMC-PACS MOU before publishing the final DPR.");
    if (
      published?.originalPdfUrl &&
      !window.confirm("Replace the current DPR with a new manual version? PACS will need to sign the new version again.")
    ) return;

    setPublishing(true);
    const uploadedRefs = [];
    try {
      const versionNumber = Number(published?.versionNumber || 0) + 1;
      const version = `V${String(versionNumber).padStart(3, "0")}`;
      const basePath = `nafed_projects/${selected.id}/dpr/${version}`;
      const pdfPath = `${basePath}/${safeName(dprFile.name)}`;
      const pdfRef = ref(storage, pdfPath);
      uploadedRefs.push(pdfRef);

      await uploadBytes(pdfRef, dprFile, {
        contentType: "application/pdf",
        customMetadata: {
          projectId: selected.id,
          applicationId: applicationId(selected),
          version,
          uploadedByUid: clean(profile.uid),
          workflow: DPR_WORKFLOW_VERSION,
          pmcSignature: "included-in-source-pdf",
        },
      });
      const originalPdfUrl = await getDownloadURL(pdfRef);

      const record = {
        projectId: selected.id,
        applicationId: applicationId(selected),
        societyName: projectName(selected),
        pacsUid: pacsOwnerUid(selected),
        allocatedPmcUid:
          clean(profile.uid) ||
          pick(selected, ["allocatedPmcUid", "assignedPmcUid", "pmcUid"]),
        status: "PMC_PUBLISHED",
        sourceType: "PMC_MANUAL_PDF",
        workflowVersion: DPR_WORKFLOW_VERSION,
        currentVersion: version,
        versionNumber,
        fileName: dprFile.name,
        fileSize: Number(dprFile.size || 0),
        mimeType: "application/pdf",
        originalPdfUrl,
        originalPdfStoragePath: pdfPath,
        pmcSignatureIncludedInPdf: true,
        pmcSignatureUrl: "",
        pmcSignatureStoragePath: "",
        pmcSignerName: "",
        pmcSignerDesignation: "",
        finalSignedDprUrl: "",
        finalSignedDprStoragePath: "",
        pacsSignatureUrl: "",
        pacsSignerName: "",
        pacsSignerDesignation: "",
        pacsSignedAtIso: "",
        publishedByUid: clean(profile.uid),
        publishedByEmail: clean(profile.email),
        publishedAtIso: new Date().toISOString(),
        publishedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const projectChanges = {
        dprStatus: "PMC_PUBLISHED",
        dprSourceType: "PMC_MANUAL_PDF",
        dprVersion: version,
        dprPdfUrl: originalPdfUrl,
        dprUrl: originalPdfUrl,
        dprOriginalPdfUrl: originalPdfUrl,
        dprOriginalStoragePath: pdfPath,
        dprPmcSignatureIncludedInPdf: true,
        dprPmcSignatureUrl: "",
        dprPmcSignerName: "",
        dprPmcSignerDesignation: "",
        dprPacsSignatureUrl: "",
        dprPacsSignerName: "",
        dprPacsSignerDesignation: "",
        finalDprUrl: "",
        finalSignedDprUrl: "",
        currentStage: "DPR Published - PACS Signature Pending",
        dprPublishedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const batch = writeBatch(db);
      batch.set(doc(db, "published_dprs", selected.id), record, { merge: true });
      batch.update(doc(db, "godown_projects", selected.id), projectChanges);
      await batch.commit();

      const activity = {
        action: "PMC_MANUAL_DPR_PUBLISHED",
        actorUid: clean(profile.uid),
        actorEmail: clean(profile.email),
        actorRole: "PMC",
        projectId: selected.id,
        pacsUid: pacsOwnerUid(selected),
        details: { version, fileName: dprFile.name, fileSize: Number(dprFile.size || 0) },
        createdAt: serverTimestamp(),
      };
      await Promise.allSettled([
        setDoc(doc(db, "published_dprs", selected.id, "versions", version), record),
        setDoc(doc(collection(db, "nafed_activity")), activity),
      ]);

      setPublished({ ...record, publishedAt: new Date(), updatedAt: new Date() });
      setDprFile(null);
      setConfirmed(false);
      onProjectUpdated?.(selected.id, { ...projectChanges, updatedAt: new Date() });
      toast.success(`${version} manual DPR published to PACS.`);
    } catch (error) {
      console.error("Manual DPR publish failed:", error);
      await Promise.allSettled(uploadedRefs.map((item) => deleteObject(item)));
      toast.error(
        error?.code === "permission-denied" || error?.code === "storage/unauthorized"
          ? "DPR upload permission denied. Allow the allocated PMC to upload DPR files and update this project."
          : error?.message || "Manual DPR could not be published.",
      );
    } finally {
      setPublishing(false);
    }
  }

  if (!eligibleProjects.length) {
    return (
      <div className="manual-dpr-empty">
        <h2>No allocated PACS project</h2>
        <p>The DPR upload desk activates after a PACS project is allocated to this PMC.</p>
      </div>
    );
  }

  const finalSignedDprUrl =
    selected?.finalSignedDprUrl ||
    selected?.finalDprUrl ||
    published?.finalSignedDprUrl;
  const publishedUrl = finalSignedDprUrl || published?.originalPdfUrl;
  return (
    <div className="manual-dpr-shell">
      <style>{DPR_STYLE}</style>
      <header className="manual-dpr-hero">
        <div>
          <small>CONTROLLED MANUAL DPR WORKFLOW</small>
          <h2>DPR Upload & Publication Desk</h2>
          <p>Upload the complete PMC-prepared and PMC-signed PDF. PACS receives the same PDF instantly and adds only its acceptance signature on the final page.</p>
        </div>
        <span className={finalSignedDprUrl ? "done" : published?.originalPdfUrl ? "live" : "new"}>
          {finalSignedDprUrl ? "PACS SIGNED FINAL" : published?.originalPdfUrl ? "PUBLISHED TO PACS" : "NEW DPR"}
        </span>
      </header>

      <div className="manual-dpr-selector">
        <label>
          <span>Allocated PACS Project</span>
          <select value={selected?.id || ""} onChange={(event) => setProjectId(event.target.value)}>
            {eligibleProjects.map((item) => (
              <option key={item.id} value={item.id}>
                {projectName(item)} · {applicationId(item)}
              </option>
            ))}
          </select>
        </label>
        <div><span>MOU Gate</span><b>{mouReady ? "Two-party MOU Complete" : "MOU Pending"}</b></div>
        <div><span>Published Version</span><b>{published?.currentVersion || "Not Published"}</b></div>
        <div><span>PACS Execution</span><b>{finalSignedDprUrl ? "Signed & Locked" : published?.originalPdfUrl ? "Signature Pending" : "Waiting"}</b></div>
      </div>

      {published?.originalPdfUrl && (
        <section className="published-dpr-card">
          <div className="published-dpr-icon">PDF</div>
          <div>
            <small>CURRENT CONTROLLED VERSION</small>
            <h3>{published.fileName || "Manual Detailed Project Report.pdf"}</h3>
            <p>{published.currentVersion} · {bytesText(published.fileSize)} · Published {dateText(published.publishedAt || published.publishedAtIso)}</p>
          </div>
          <div className="published-dpr-actions">
            <button type="button" onClick={() => window.open(publishedUrl, "_blank", "noopener,noreferrer")}>Preview</button>
            <button type="button" onClick={() => downloadFile(publishedUrl, finalSignedDprUrl ? `FINAL_DPR_${applicationId(selected)}_${published.currentVersion}.pdf` : published.fileName)}>Download</button>
          </div>
        </section>
      )}

      <section className="manual-dpr-form">
        <div className="manual-dpr-section-title">
          <span>01</span>
          <div><small>PMC CONTROLLED SUBMISSION</small><h3>{published?.originalPdfUrl ? "Replace Manual DPR Version" : "Publish Manual DPR"}</h3></div>
        </div>
        <div className="manual-dpr-fields">
          <label className="wide">
            <span>PMC-Signed Final DPR PDF * <em>Maximum 75 MB</em></span>
            <input type="file" accept="application/pdf,.pdf" onChange={(event) => setDprFile(event.target.files?.[0] || null)} />
            <small>{dprFile ? `${dprFile.name} · ${bytesText(dprFile.size)}` : "Upload the complete DPR prepared and already signed by the PMC."}</small>
          </label>
          <div className="manual-dpr-pmc-proof wide"><b>PMC signature is already inside this PDF</b><span>No separate PMC signature image is required or added again. The uploaded source pages remain unchanged.</span></div>
        </div>
        <label className="manual-dpr-confirm">
          <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
          <span>I confirm that the uploaded PDF is the complete PMC-prepared DPR, it already contains the authorised PMC signature, and all pages have been verified for PACS publication.</span>
        </label>
        {!mouReady && <div className="manual-dpr-gate"><b>Publication locked:</b> complete the PMC-PACS two-party MOU first. File selection remains available.</div>}
        <footer>
          <div><b>{published?.originalPdfUrl ? "Publishing creates the next version." : "PACS gets live download access after publication."}</b><span>A replacement clears old PACS execution and requires a fresh PACS signature.</span></div>
          <button type="button" disabled={publishing || loading || !mouReady} onClick={publishManualDpr}>
            {publishing ? "Uploading & Publishing…" : published?.originalPdfUrl ? "Publish Replacement Version" : "Publish DPR to PACS"}
          </button>
        </footer>
      </section>
    </div>
  );
}

const DPR_STYLE = `
.manual-dpr-shell{--navy:#10375f;--green:#0b7545;--ink:#18324a;--muted:#66798b;--line:#d3dee8;color:var(--ink);font-family:Inter,"Segoe UI",Arial,sans-serif}.manual-dpr-shell *{box-sizing:border-box}.manual-dpr-hero{display:flex;align-items:flex-start;justify-content:space-between;gap:22px;padding:23px;border:1px solid #b8d5c5;border-top:5px solid var(--green);border-radius:12px;background:linear-gradient(120deg,#fff,#eef8f3)}.manual-dpr-hero small,.manual-dpr-section-title small,.published-dpr-card small{color:var(--green);font-size:9px;font-weight:900;letter-spacing:.09em}.manual-dpr-hero h2{margin:6px 0;color:var(--navy);font-size:27px}.manual-dpr-hero p{max-width:800px;margin:0;color:var(--muted);font-size:11px;line-height:1.6}.manual-dpr-hero>span{padding:9px 12px;border-radius:20px;font-size:9px;font-weight:900;white-space:nowrap}.manual-dpr-hero>span.new{background:#eef2f6;color:#53677a}.manual-dpr-hero>span.live{background:#fff4dc;color:#8b5b00}.manual-dpr-hero>span.done{background:#e6f7ec;color:#08713e}.manual-dpr-selector{display:grid;grid-template-columns:minmax(320px,2fr) repeat(3,1fr);gap:10px;margin:13px 0}.manual-dpr-selector>label,.manual-dpr-selector>div{min-height:72px;padding:13px;border:1px solid var(--line);border-radius:9px;background:#fff}.manual-dpr-selector span,.manual-dpr-selector b{display:block}.manual-dpr-selector span{color:var(--muted);font-size:9px}.manual-dpr-selector b{margin-top:7px;color:var(--navy);font-size:12px}.manual-dpr-selector select{width:100%;margin-top:6px;border:0;background:#fff;color:var(--navy);font-size:12px;font-weight:800;outline:0}.published-dpr-card{display:grid;grid-template-columns:auto 1fr auto;align-items:center;gap:14px;margin-bottom:13px;padding:15px;border:1px solid #a9ceb8;border-radius:10px;background:#f3fbf6}.published-dpr-icon{width:48px;height:48px;display:grid;place-items:center;border-radius:9px;background:#c92f38;color:#fff;font-size:10px;font-weight:950}.published-dpr-card h3{margin:4px 0;color:var(--navy);font-size:14px}.published-dpr-card p{margin:0;color:var(--muted);font-size:9px}.published-dpr-actions{display:flex;gap:7px}.published-dpr-actions button{padding:9px 12px;border:1px solid #9eb3c5;border-radius:6px;background:#fff;color:var(--navy);font-size:9px;font-weight:900;cursor:pointer}.published-dpr-actions button+button{border-color:var(--green);background:var(--green);color:#fff}.manual-dpr-form{overflow:hidden;border:1px solid var(--line);border-radius:11px;background:#fff}.manual-dpr-section-title{display:flex;align-items:center;gap:12px;padding:16px 18px;border-bottom:1px solid var(--line);background:#f7f9fb}.manual-dpr-section-title>span{width:35px;height:35px;display:grid;place-items:center;border-radius:50%;background:var(--navy);color:#fff;font-size:9px;font-weight:900}.manual-dpr-section-title h3{margin:4px 0 0;color:var(--navy);font-size:16px}.manual-dpr-fields{display:grid;grid-template-columns:1fr 1fr;gap:13px;padding:18px}.manual-dpr-fields label{display:block}.manual-dpr-fields label.wide,.manual-dpr-fields .wide{grid-column:1/-1}.manual-dpr-fields label>span{display:block;margin-bottom:6px;color:#405a70;font-size:9px;font-weight:850}.manual-dpr-fields label>span em{float:right;color:var(--muted);font-style:normal;font-weight:600}.manual-dpr-fields input{width:100%;min-height:42px;padding:10px;border:1px solid #b9c8d5;border-radius:6px;background:#fff;color:var(--ink);font:inherit;font-size:11px;outline:0}.manual-dpr-fields input:focus{border-color:var(--green);box-shadow:0 0 0 3px #e3f3ea}.manual-dpr-fields label>small{display:block;margin-top:6px;color:var(--muted);font-size:8.5px}.manual-dpr-pmc-proof{padding:14px;border:1px solid #a9ceb8;border-left:4px solid var(--green);border-radius:7px;background:#f2faf5}.manual-dpr-pmc-proof b,.manual-dpr-pmc-proof span{display:block}.manual-dpr-pmc-proof b{color:var(--green);font-size:11px}.manual-dpr-pmc-proof span{margin-top:5px;color:var(--muted);font-size:9px}.manual-dpr-confirm{display:flex;gap:9px;align-items:flex-start;margin:0 18px 15px;padding:12px;border:1px solid #cbd7e0;background:#f6f8fa;border-radius:7px;color:#465e72;font-size:9px;line-height:1.5}.manual-dpr-confirm input{margin-top:2px}.manual-dpr-gate{margin:0 18px 15px;padding:11px;border:1px solid #e5c378;border-left:4px solid #ce851d;border-radius:6px;background:#fff8e8;color:#79500b;font-size:9px}.manual-dpr-form footer{display:flex;align-items:center;gap:15px;padding:14px 18px;border-top:1px solid var(--line);background:#f7f9fb}.manual-dpr-form footer>div{margin-right:auto}.manual-dpr-form footer b,.manual-dpr-form footer span{display:block}.manual-dpr-form footer b{color:var(--navy);font-size:9px}.manual-dpr-form footer span{margin-top:3px;color:var(--muted);font-size:8px}.manual-dpr-form footer button{min-width:205px;padding:12px 16px;border:0;border-radius:7px;background:var(--green);color:#fff;font-size:10px;font-weight:900;cursor:pointer}.manual-dpr-form footer button:disabled{cursor:not-allowed;opacity:.45}.manual-dpr-empty{padding:40px;border:1px dashed #b8c7d3;border-radius:11px;background:#fff;text-align:center;color:#607487}@media(max-width:1000px){.manual-dpr-selector{grid-template-columns:1fr 1fr}}@media(max-width:700px){.manual-dpr-hero{display:block}.manual-dpr-hero>span{display:inline-block;margin-top:12px}.manual-dpr-selector,.manual-dpr-fields{grid-template-columns:1fr}.manual-dpr-fields label.wide,.manual-dpr-fields .wide{grid-column:auto}.published-dpr-card{grid-template-columns:auto 1fr}.published-dpr-actions{grid-column:1/-1}.manual-dpr-form footer{align-items:stretch;flex-direction:column}.manual-dpr-form footer button{width:100%}}
`;
